import { useEffect, useMemo, useState } from 'react';
import { authMode, devSignIn, restoreSession, signInWithMicrosoft, type Session } from '@shared/auth';
import { createApi } from '@shared/api';
import type { MeDto } from '@shared/types';
import { AppProvider, useApp } from './context';
import { match, navigate, useRoute } from './router';
import { Dashboard } from './pages/Dashboard';
import { DrawingWizard } from './pages/DrawingWizard';
import { DrawingLive } from './pages/DrawingLive';
import { Winners } from './pages/Winners';
import { Employees, EmployeeDetail } from './pages/Employees';
import { Rules } from './pages/Rules';
import { Crews } from './pages/Crews';
import { Audit } from './pages/Audit';
import { Integration } from './pages/Integration';
import { More } from './pages/More';
import { DrawingsList } from './pages/DrawingsList';
import { Award } from './pages/Award';

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [authError, setAuthError] = useState<string | null>(null);
  useEffect(() => { restoreSession().then(setSession).catch((e: Error) => { setAuthError(e.message); setSession(null); }); }, []);

  if (session === undefined) return <div className="boot"><img src="./brand/kes-logo-reversed.png" alt="KES" /></div>;
  if (!session) return <Login error={authError} onDev={(u) => setSession(devSignIn({ user: u, name: u.split('@')[0]!, roles: ['RewardsAdmin'] }))} />;
  return <Authed session={session} onSignedOut={() => setSession(null)} />;
}

function Authed({ session, onSignedOut }: { session: Session; onSignedOut: () => void }) {
  const api = useMemo(() => createApi(session, undefined), [session]);
  const [me, setMe] = useState<MeDto | null | 'error'>(null);
  useEffect(() => { api.get<MeDto>('/api/me').then(setMe).catch(() => setMe('error')); }, [api]);

  const signOut = async () => { await session.signOut(); onSignedOut(); };
  if (me === null) return <div className="boot"><img src="./brand/kes-logo-reversed.png" alt="KES" /><p>Checking access…</p></div>;
  if (me === 'error') return <Denied text="Could not reach the Rewards API. Check your connection and try again." onSignOut={signOut} />;
  // Authorization is decided by the API from the Entra app role; this only reflects it.
  if (!me.roles.includes('RewardsAdmin'))
    return <Denied text={`${me.user} is signed in but is not assigned the Rewards Admin role. Ask an Entra administrator to assign RewardsAdmin.`} onSignOut={signOut} />;

  return (
    <AppProvider api={api} session={session}>
      <Shell onSignOut={signOut} />
    </AppProvider>
  );
}

function Shell({ onSignOut }: { onSignOut: () => void }) {
  const route = useRoute();
  const { online, hubStatus } = useApp();
  let page: JSX.Element;
  let m: Record<string, string> | null;
  if ((m = match('/draw/:type', route))) page = <DrawingWizard key={route} drawingType={m.type!} />;
  else if ((m = match('/drawings/:id/live', route))) page = <DrawingLive key={m.id} drawingId={m.id!} />;
  else if ((m = match('/drawings/:id', route))) page = <DrawingWizard key={m.id} drawingId={m.id!} />;
  else if (match('/drawings', route)) page = <DrawingsList />;
  else if ((m = match('/award/:id', route))) page = <Award key={m.id} employeeId={m.id!} />;
  else if (match('/award', route)) page = <Award />;
  else if (match('/winners', route)) page = <Winners />;
  else if ((m = match('/employees/:id', route))) page = <EmployeeDetail key={m.id} employeeId={m.id!} />;
  else if (match('/employees', route)) page = <Employees />;
  else if (match('/rules', route)) page = <Rules />;
  else if (match('/crews', route)) page = <Crews />;
  else if (match('/audit', route)) page = <Audit />;
  else if (match('/integration', route)) page = <Integration />;
  else if (match('/more', route)) page = <More onSignOut={onSignOut} />;
  else page = <Dashboard />;

  const tab = route.startsWith('/winners') ? 'winners' : route.startsWith('/employees') || route.startsWith('/award') ? 'employees'
    : route.startsWith('/draw') ? 'drawings' : route === '/' || route === '' ? 'home' : 'more';

  return (
    <div className="shell">
      <div className={`netbar ${online ? (hubStatus === 'connected' ? 'net-ok' : 'net-warn') : 'net-bad'}`} role="status">
        <span className="net-dot" />
        {online ? (hubStatus === 'connected' ? 'Online' : 'Online — live updates reconnecting') : 'Offline — drawings cannot be started'}
      </div>
      <main className="main">{page}</main>
      <nav className="tabbar" aria-label="Main">
        <Tab id="home" cur={tab} to="/" label="Dashboard" icon={<path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />} />
        <Tab id="drawings" cur={tab} to="/drawings" label="Drawings" icon={<><circle cx="12" cy="12" r="9" /><path d="M12 3v9l6 6" /></>} />
        <Tab id="winners" cur={tab} to="/winners" label="Winners" icon={<path d="M7 4h10v3a5 5 0 0 1-10 0zM5 5H3v2a4 4 0 0 0 4 4M19 5h2v2a4 4 0 0 1-4 4M9 14h6l1 4H8zM7 20h10" />} />
        <Tab id="employees" cur={tab} to="/employees" label="Employees" icon={<><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M16 4a4 4 0 0 1 0 8M22 21a7 7 0 0 0-5-6.7" /></>} />
        <Tab id="more" cur={tab} to="/more" label="More" icon={<><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></>} />
      </nav>
    </div>
  );
}

function Tab({ id, cur, to, label, icon }: { id: string; cur: string; to: string; label: string; icon: JSX.Element }) {
  return (
    <button className={`tab ${cur === id ? 'on' : ''}`} onClick={() => navigate(to)} aria-current={cur === id ? 'page' : undefined}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
      <span>{label}</span>
    </button>
  );
}

const DEV_USERS = ['derek@kes-llc.com', 'sam@kes-llc.com', 'tawnya@kes-llc.com', 'tony@kes-llc.com', 'wesley@kes-llc.com'];

function Login({ error, onDev }: { error: string | null; onDev: (user: string) => void }) {
  return (
    <div className="login">
      <div className="login-card">
        <img src="./brand/kes-logo.png" alt="Keystone Energy Services" className="login-logo" />
        <h1>Safety Accountability Program</h1>
        <p className="login-sub">Admin PWA</p>
        {authMode === 'entra' ? (
          <button className="btn btn-ms" onClick={() => void signInWithMicrosoft().catch(() => undefined)}>
            <svg viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" /><rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" /></svg>
            Sign in with Microsoft
          </button>
        ) : (
          <>
            <p className="login-dev">Local development sign-in. Production uses Microsoft Entra ID.</p>
            {DEV_USERS.map((u) => <button key={u} className="btn btn-secondary btn-block" onClick={() => onDev(u)}>Continue as {u}</button>)}
          </>
        )}
        {error && <p className="login-error">{error}</p>}
        <p className="login-foot">Access is limited to KES accounts assigned the Rewards Admin role.</p>
      </div>
    </div>
  );
}

function Denied({ text, onSignOut }: { text: string; onSignOut: () => void }) {
  return (
    <div className="login">
      <div className="login-card">
        <img src="./brand/kes-logo.png" alt="Keystone Energy Services" className="login-logo" />
        <h1>Access required</h1>
        <p>{text}</p>
        <button className="btn btn-secondary btn-block" onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}
